import { Icon } from '@iconify/react';
import { FieldWrapper } from '@progress/kendo-react-form';
import { InputPrefix, TextBox, TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { Hint, Label } from '@progress/kendo-react-labels';
import { TabStrip, TabStripSelectEventArguments, TabStripTab } from '@progress/kendo-react-layout';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CatalogTreeViewDropdown from 'src/components/design-system/CatalogTreeViewDropdown';
import { CatalogTreeViewItem } from 'src/types/catalog/Catalog';
import { findCatalogTreeItem } from 'src/utils/tansformCatalogsToTreeData';
import { useContextSelector } from 'use-context-selector';
import {
  catalogTreeItemToSearchOption,
  hasRefineSearchInputError,
} from './searchProductContext.utils';
import { SearchProductFiltersContext } from './SearchProductFiltersContext';
import { SearchProductQueryContext } from './SearchProductQueryContext';
import { SearchProductSelectionsContext } from './SearchProductSelectionsContext';

const SearchProductFilters = () => {
  const { t } = useTranslation();

  const searchTerm = useContextSelector(SearchProductQueryContext, (context) => context.searchTerm);
  const selectedView = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.selectedView
  );
  const handleViewChange = useContextSelector(
    SearchProductSelectionsContext,
    (context) => context.handleViewChange
  );
  const refineSearch = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.refineSearch
  );
  const handleRefineSearchChange = useContextSelector(
    SearchProductSelectionsContext,
    (context) => context.handleRefineSearchChange
  );
  const catalogTreeData = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.catalogTreeData
  );
  const catalogDropdownPlaceholder = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.catalogDropdownPlaceholder
  );
  const selectedCatalogFilter = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.selectedCatalogFilter
  );
  const setSelectedCatalogFilter = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.setSelectedCatalogFilter
  );
  const setDataState = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.setDataState
  );
  const handleConfirm = useContextSelector(
    SearchProductSelectionsContext,
    (context) => context.handleConfirm
  );

  const selectedCatalogValue = useMemo(
    () =>
      findCatalogTreeItem(catalogTreeData, selectedCatalogFilter.catalogId) ??
      catalogTreeData[0] ??
      null,
    [catalogTreeData, selectedCatalogFilter.catalogId]
  );

  const handleTabChange = useCallback(
    (e: TabStripSelectEventArguments): void => {
      void handleViewChange(e.selected);
    },
    [handleViewChange]
  );

  const handleCatalogFilterChange = useCallback(
    async (item: CatalogTreeViewItem | null) => {
      if (!item) {
        return;
      }

      await handleConfirm(() => {
        setSelectedCatalogFilter(catalogTreeItemToSearchOption(item));
        setDataState((prev) => ({ ...prev, skip: 0 }));
      });
    },
    [handleConfirm, setDataState, setSelectedCatalogFilter]
  );

  const displaySearchText = searchTerm.trim();
  const refineSearchError = hasRefineSearchInputError(refineSearch)
    ? t('Please enter at least 3 letters to search')
    : undefined;

  return (
    <div className="w-full px-4">
      {displaySearchText && (
        <div className="truncate pt-4 pb-3 text-sm font-semibold">
          {t('Showing results for')} &quot;{displaySearchText}&quot;
        </div>
      )}

      <div
        className={`flex items-start justify-start gap-4 mb-2 flex-wrap w-full${
          displaySearchText ? '' : ' mt-4'
        }`}
      >
        <TabStrip
          className="min-w-[190px] flex-shrink-0"
          selected={selectedView}
          onSelect={handleTabChange}
        >
          <TabStripTab contentClassName="!hidden" title={t('List View')} />
          <TabStripTab contentClassName="!hidden" title={t('Picture View')} />
        </TabStrip>

        <div className="flex items-center gap-2 flex-[1_1_250px] min-w-[200px] max-w-[350px]">
          <span className="text-xs flex items-center whitespace-nowrap flex-shrink-0">
            {t('Select Catalog')}
          </span>
          <div className="flex-1 min-w-0">
            <CatalogTreeViewDropdown
              data={catalogTreeData}
              onChange={handleCatalogFilterChange}
              value={selectedCatalogValue}
              placeholder={catalogDropdownPlaceholder}
              width="100%"
            />
          </div>
        </div>

        <div className="flex-[1_1_250px] min-w-[200px] max-w-[350px]">
          <FieldWrapper className="grid grid-cols-[auto_1fr] items-center gap-x-2 gap-y-0.5">
            <Label
              editorId="refine-search-input"
              className="text-xs flex items-center whitespace-nowrap flex-shrink-0"
            >
              {t('Refine Search')}
            </Label>
            <TextBox
              id="refine-search-input"
              max={500}
              maxLength={500}
              size="medium"
              style={{ fontSize: '14px' }}
              placeholder={t('Enter Item # or Description')}
              width="100%"
              value={refineSearch}
              valid={!refineSearchError}
              onChange={async (e: TextBoxChangeEvent): Promise<void> => {
                await handleRefineSearchChange(e.value as string);
              }}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              prefix={() => (
                <InputPrefix className="pl-2">
                  <Icon height="18" width="18" color="#AAAAAA" icon="lucide:search" />
                </InputPrefix>
              )}
            />
            {refineSearchError && (
              <>
                <span aria-hidden="true" />
                <Hint id="refine-search-input-hint" className="!text-error">
                  {refineSearchError}
                </Hint>
              </>
            )}
          </FieldWrapper>
        </div>
      </div>
    </div>
  );
};

export default SearchProductFilters;
